// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import EmptyState from "@/components/common/EmptyState";
import { GripVertical, ListTodo, Pencil } from "lucide-react";
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PHASE_LABELS } from "@/utils/deals.utils";
import { resolveTaskOwnership } from "@/utils/task-ownership.utils";
import { formatUtcDate } from "@/utils/common.utils";
import SectionCard from "@/components/common/SectionCard";
import { TaskStatus } from "@/enum/common.enum";

function formatDueRule(task) {
  if (task.due_date_source === "relative_to_closing") {
    const offset = Number(task.due_date_offset_days);
    if (!Number.isFinite(offset)) return "Before closing";
    if (offset === 0) return "At closing";
    return `${offset} day${offset === 1 ? "" : "s"} before closing`;
  }
  if (task.due_date) return "Specific date";
  return "—";
}

/** Drag state for one row: the ref and style for the row, and the props that
 *  make its grip the drag handle (pointer, touch and keyboard). */
export function SortableTask({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return children({
    ref: setNodeRef,
    style: { transform: CSS.Translate.toString(transform), transition, ...(isDragging ? { position: "relative", zIndex: 10 } : {}) },
    handle: { ...attributes, ...listeners },
    isDragging,
  });
}

export function DragHandle({ handle, title }) {
  return (
    <button type="button" {...handle} className="shrink-0 cursor-grab touch-none rounded p-1 text-dash-faint hover:bg-dash-hover hover:text-dash-ink active:cursor-grabbing" aria-label={`Move ${title}`} data-testid="task-drag-handle">
      <GripVertical className="size-4" />
    </button>
  );
}

/** Checklist order is the agent's to set: rows drag by their grip, and the new
 *  order is handed to `onReorder` as the full list of visible ids. Without
 *  `onReorder` (a search is narrowing the list) there are no grips. */
export function SortableList({ ids, onReorder, children }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  if (!onReorder) return children;
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
}

export default function TaskTable({ tasks, deal, onToggle, onNudge, onEditTask, onReorder = null }) {
  const ids = tasks.map((task) => task.id);
  const sortable = Boolean(onReorder);
  return (
    <>
      <SortableList ids={ids} onReorder={onReorder}>
      <div className="flex flex-col gap-3 md:hidden">
        {tasks.map((task) => {
          const complete = task.status === TaskStatus.COMPLETE;
          return (
            <SortableTask key={task.id} id={task.id} disabled={!sortable}>
            {({ ref, style, handle }) => (
            <article ref={ref} style={style} className={`rounded-xl border border-dash-border bg-white p-4 ${task.waived_at ? "opacity-60" : ""}`}>
              <div className="flex items-start gap-3">
                {sortable && <DragHandle handle={handle} title={task.title} />}
                <Checkbox checked={complete} onCheckedChange={() => onToggle?.(task)} aria-label={`Mark ${task.title} as ${complete ? "not completed" : "completed"}`} className="mt-0.5 shrink-0 border-dash-muted data-[state=checked]:border-primary" />
                <div className="min-w-0 flex-1">
                  <p className={complete ? "break-words text-sm text-dash-faint line-through" : "break-words text-sm font-medium text-dash-ink"}>{task.title}</p>
                  {task.waived_at && <p className="mt-0.5 text-xs text-dash-faint">Not needed · hidden from the client</p>}
                </div>
                {onEditTask && (
                  <button type="button" onClick={() => onEditTask(task)} className="shrink-0 rounded p-1 text-dash-faint hover:bg-dash-hover hover:text-dash-ink" aria-label={`Edit ${task.title}`}>
                    <Pencil className="size-3.5" />
                  </button>
                )}
              </div>
              {onNudge && task.status !== "complete" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onNudge(task)}>Nudge owner</Button>
                </div>
              )}
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-dash-border pt-4 text-xs">
                <div><dt className="mb-1 uppercase tracking-wide text-dash-faint">Act</dt><dd className="text-dash-muted">{PHASE_LABELS[task.phase] || task.phase}</dd></div>
                <div><dt className="mb-1 uppercase tracking-wide text-dash-faint">Due</dt><dd className={task.status === "overdue" ? "text-dash-rust" : "text-dash-ink"}>{task.due_date ? formatUtcDate(task.due_date) : "—"}</dd></div>
                <div className="col-span-2"><dt className="mb-1 uppercase tracking-wide text-dash-faint">Due Rule</dt><dd className="text-dash-muted">{formatDueRule(task)}</dd></div>
                <div className="col-span-2"><dt className="mb-1 uppercase tracking-wide text-dash-faint">Assignee</dt><dd><TaskAssignee task={task} deal={deal} /></dd></div>
              </dl>
            </article>
            )}
            </SortableTask>
          );
        })}
        {!tasks.length && <EmptyState compact icon={ListTodo} title="No matching tasks" description="Try changing the search term or selected phase." className="border border-dash-border bg-white" />}
      </div>
      </SortableList>
      <SectionCard className="hidden overflow-hidden md:block" contentClassName="p-0">
        <Table className="min-w-[880px]">
        <TableHeader className="bg-dash-page">
          <TableRow className="hover:bg-transparent">
            {sortable && <TableHead className="h-11 w-10 px-2"><span className="sr-only">Reorder</span></TableHead>}
            {['Task', 'Act', 'Assignee', 'Due', 'Due Rule', ...(onEditTask ? ['Actions'] : [])].map((heading) => (
              <TableHead key={heading} className="h-11 px-5 text-[11px] font-medium uppercase tracking-wide text-dash-faint">{heading}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <SortableList ids={ids} onReorder={onReorder}>
        <TableBody>
          {tasks.map((task) => {
            const complete = task.status === TaskStatus.COMPLETE;
            return (
              <SortableTask key={task.id} id={task.id} disabled={!sortable}>
              {({ ref, style, handle, isDragging }) => (
              <TableRow ref={ref} style={style} className={`h-[76px] border-dash-border hover:bg-dash-hover/50 ${isDragging ? "bg-white shadow-md" : ""} ${task.waived_at ? "opacity-60" : ""}`}>
                {sortable && <TableCell className="w-10 px-2 py-4"><DragHandle handle={handle} title={task.title} /></TableCell>}
                <TableCell className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <Checkbox checked={complete} onCheckedChange={() => onToggle?.(task)} aria-label={`Mark ${task.title} as ${complete ? "not completed" : "completed"}`} className="border-dash-muted data-[state=checked]:border-primary" />
                    <span className="flex flex-col">
                      <span className={complete ? "text-dash-faint line-through" : "font-medium text-dash-ink"}>{task.title}</span>
                      {task.waived_at && <span className="text-xs text-dash-faint">Not needed · hidden from the client</span>}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="px-5 py-4 text-dash-muted">{PHASE_LABELS[task.phase] || task.phase}</TableCell>
                <TableCell className="px-5 py-4"><TaskAssignee task={task} deal={deal} />{onNudge && task.status !== "complete" && <Button variant="ghost" size="sm" className="mt-1 px-0 text-xs" onClick={() => onNudge(task)}>Nudge</Button>}</TableCell>
                <TableCell className={`px-5 py-4 ${task.status === "overdue" ? "text-dash-rust" : "text-dash-ink"}`}>
                  {task.due_date ? formatUtcDate(task.due_date) : "—"}
                </TableCell>
                <TableCell className="px-5 py-4 text-dash-muted">{formatDueRule(task)}</TableCell>
                {onEditTask && (
                  <TableCell className="px-5 py-4">
                    <Button variant="ghost" size="sm" className="gap-1.5 px-2 text-xs text-dash-muted hover:text-dash-ink" onClick={() => onEditTask(task)} aria-label={`Edit ${task.title}`}>
                      <Pencil className="size-3.5" /> Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
              )}
              </SortableTask>
            );
          })}
          {!tasks.length && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={(onEditTask ? 6 : 5) + (sortable ? 1 : 0)} className="p-0">

                <EmptyState compact icon={ListTodo} title="No matching tasks" description="Try changing the search term or selected phase." className="rounded-none border-0" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </SortableList>
        </Table>
      </SectionCard>
    </>
  );
}

/**
 * Who owes this row.
 *
 * This cell rendered blank on every task until now: it called
 * `resolveRole(task.assigned_role)` with a string, and `resolveRole` destructures
 * an object, so `role` came through undefined and its `if (!role) return null`
 * guard fired every time. The column has therefore never shown anything, on any
 * deal — which is most of why the client portal could not say who was working.
 *
 * Names the person when the deal has one and falls back to the role when it does
 * not, because "Lender" is a true statement about an empty seat and a blank is
 * not. The unfilled case is deliberately muted rather than styled as an error:
 * a lender who has not been invited yet is normal at this stage of a deal, not a
 * fault to shout about.
 */
function TaskAssignee({ task, deal }) {
  const owner = resolveTaskOwnership(task, deal);
  return (
    <span className="flex flex-col" data-testid={`task-assignee-${task.id}`}>
      <span className={owner.state === "named" ? "text-dash-ink" : "text-dash-muted"}>
        {owner.label}
      </span>
      {owner.hint && <span className="text-xs text-dash-faint">{owner.hint}</span>}
    </span>
  );
}
